/**
 * 생년월일 고르기 (귀인도감 client/src/components/pickers/DateField.jsx 방식, 사용자 요청 2026-10-14, D78).
 *
 * 브라우저 기본 date 칸 대신 쓰는 이유: 생년월일은 수십 년을 거슬러 올라가야 하는데 기본 칸은 연도 이동이 느리고,
 * OS 마다 생김새가 달라 디자인과 맞지 않는다.
 *
 * 연 → 월 → 일 3단:
 *  - 연: 1900년 ~ 올해 전체를 4열 그리드로 펼쳐 스크롤. 아직 안 골랐으면 흔한 출생연도(올해 - 25) 근처에서 시작
 *  - 월: 12칸. 고르면 일로
 *  - 일: 양력이면 요일 머리가 있는 달력(오늘 뒤는 못 고름), 음력이면 1~30일 칸(요일 없음)
 *  - 위의 「1995년」 「5월」 칩을 눌러 언제든 앞 단계로, 일 화면에서는 ‹ › 로 달을 넘김
 *  - 아무것도 안 고른 상태를 미리 채워 보이지 않음(「생년월일을 선택해주세요」)
 * 시트 틀 · 칸은 PickerDialog.jsx(태어난 시각 고르기와 같이 씀).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PickerDialog, PickerField } from './PickerDialog.jsx';

export const MIN_YEAR = 1900;
const PANE = { YEAR: 'year', MONTH: 'month', DAY: 'day' };
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
// 음력 한 달은 29 · 30일. 그해 그달이 29일이면 서버가 알려 줌(validateBirth)
const LUNAR_DAYS = 30;

const pad = (number) => String(number).padStart(2, '0');

export function parseBirthDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''));
    return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : null;
}

export function formatBirthDate(parts) {
    return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function birthDateLabel(parts) {
    return `${parts.year}년 ${parts.month}월 ${parts.day}일`;
}

function todayParts() {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
}

function compareDate(a, b) {
    return a.year - b.year || a.month - b.month || a.day - b.day;
}

/** 달이 바뀌어 그날이 없으면 그달 마지막 날로 */
function clampDay(year, month, day, lunar) {
    return Math.min(day, lunar ? LUNAR_DAYS : daysInMonth(year, month));
}

/**
 * 폼 안의 생년월일 칸(누르면 고르기 시트)
 * @param {{ value: string, onChange: (value: string) => void, calendarType?: 'solar'|'lunar' }} props
 */
export function BirthDateField({ value, onChange, calendarType = 'solar', label = '생년월일' }) {
    const [open, setOpen] = useState(false);
    const selected = parseBirthDate(value);
    return (
        <>
            <PickerField
                label={label}
                labelId="birth-date-label"
                valueText={selected ? `${calendarType === 'lunar' ? '음력 ' : ''}${birthDateLabel(selected)}` : null}
                placeholder="생년월일 선택"
                onOpen={() => setOpen(true)}
            />
            {open ? (
                <BirthDateSheet
                    initial={selected}
                    lunar={calendarType === 'lunar'}
                    onClose={() => setOpen(false)}
                    onConfirm={(parts) => {
                        onChange(formatBirthDate(parts));
                        setOpen(false);
                    }}
                />
            ) : null}
        </>
    );
}

export function BirthDateSheet({ initial = null, lunar = false, onClose, onConfirm }) {
    const max = todayParts();
    const [pane, setPane] = useState(initial ? PANE.DAY : PANE.YEAR);
    const [draft, setDraft] = useState(initial ?? { year: null, month: null, day: null });

    const setYear = (year) => {
        setDraft((d) => ({
            year,
            month: d.month,
            day: d.month != null && d.day != null ? clampDay(year, d.month, d.day, lunar) : d.day,
        }));
        setPane(PANE.MONTH);
    };
    const setMonth = (month) => {
        setDraft((d) => ({ ...d, month, day: d.day != null ? clampDay(d.year, month, d.day, lunar) : d.day }));
        setPane(PANE.DAY);
    };
    const stepMonth = (delta) => {
        setDraft((d) => {
            const index = d.year * 12 + (d.month - 1) + delta;
            const next = { year: Math.floor(index / 12), month: (index % 12) + 1 };
            if (next.year < MIN_YEAR || compareDate({ ...next, day: 1 }, max) > 0) {
                return d;
            }
            return { ...next, day: d.day != null ? clampDay(next.year, next.month, d.day, lunar) : null };
        });
    };

    const complete = draft.year != null && draft.month != null && draft.day != null;
    // 음력 날짜는 양력 오늘과 견주지 않음(서버가 바꿔 확인)
    const overMax = complete && !lunar && compareDate(draft, max) > 0;

    return (
        <PickerDialog
            eyebrow={lunar ? '음력' : '양력'}
            title="생년월일"
            ariaLabel="생년월일 고르기"
            confirmLabel={complete ? `${birthDateLabel(draft)} 선택` : '생년월일을 선택해주세요'}
            confirmDisabled={!complete || overMax}
            onConfirm={() => onConfirm(draft)}
            onClose={onClose}
        >
            <PaneHeader draft={draft} pane={pane} onPane={setPane} onStepMonth={stepMonth} />
            {pane === PANE.YEAR ? <YearPane year={draft.year} maxYear={max.year} onSelect={setYear} /> : null}
            {pane === PANE.MONTH ? <MonthPane draft={draft} max={max} lunar={lunar} onSelect={setMonth} /> : null}
            {pane === PANE.DAY ? (
                <DayPane draft={draft} max={max} lunar={lunar} onSelect={(day) => setDraft((d) => ({ ...d, day }))} />
            ) : null}
        </PickerDialog>
    );
}

function Cell({ selected = false, disabled = false, label, onClick, ...rest }) {
    return (
        <button
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            onClick={onClick}
            className={`flex h-11 items-center justify-center rounded-xl text-[16px] tabular-nums ${
                selected ? 'bg-brand font-semibold text-on-brand' : 'text-text-primary'
            } disabled:opacity-30`}
            {...rest}
        >
            {label}
        </button>
    );
}

/** 고른 연 · 월 칩(눌러 그 단계로), 일 화면이면 ‹ › */
function PaneHeader({ draft, pane, onPane, onStepMonth }) {
    const chip = (active, label, onClick) => (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`h-8 rounded-full border px-3 text-[13px] font-semibold ${
                active ? 'border-brand bg-brand text-on-brand' : 'border-hairline bg-surface text-text-primary'
            }`}
        >
            {label}
        </button>
    );
    if (draft.year == null) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
                {chip(pane === PANE.YEAR, `${draft.year}년`, () => onPane(PANE.YEAR))}
                {draft.month != null ? chip(pane === PANE.MONTH, `${draft.month}월`, () => onPane(PANE.MONTH)) : null}
                {draft.day != null ? (
                    <span className="flex h-8 items-center rounded-full border border-brand bg-brand px-3 text-[13px] font-semibold text-on-brand">
                        {draft.day}일
                    </span>
                ) : null}
            </div>
            {pane === PANE.DAY ? (
                <div className="flex gap-1.5">
                    <button
                        type="button"
                        aria-label="이전 달"
                        onClick={() => onStepMonth(-1)}
                        className="flex size-8 items-center justify-center rounded-full border border-hairline"
                    >
                        <ChevronLeft size={18} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        aria-label="다음 달"
                        onClick={() => onStepMonth(1)}
                        className="flex size-8 items-center justify-center rounded-full border border-hairline"
                    >
                        <ChevronRight size={18} strokeWidth={1.8} aria-hidden="true" />
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function YearPane({ year, maxYear, onSelect }) {
    const scrollRef = useRef(null);
    const years = useMemo(
        () => Array.from({ length: maxYear - MIN_YEAR + 1 }, (_, index) => maxYear - index),
        [maxYear]
    );
    // 안 골랐으면 맨 위(올해)가 아니라 흔한 출생연도 근처에서 시작
    const anchorYear = year ?? Math.max(MIN_YEAR, maxYear - 25);
    useEffect(() => {
        const box = scrollRef.current;
        const target = box?.querySelector(`[data-year="${anchorYear}"]`);
        if (box && target) {
            box.scrollTop = target.offsetTop - box.clientHeight / 2 + target.clientHeight / 2;
        }
        // 열 때 한 번
    }, []);
    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-[13px] font-semibold text-text-secondary">태어난 연도를 선택하세요</span>
            <div
                ref={scrollRef}
                role="group"
                aria-label="연도"
                className="relative grid max-h-[232px] grid-cols-4 gap-1 overflow-y-auto rounded-xl border border-hairline p-1.5"
            >
                {years.map((item) => (
                    <Cell
                        key={item}
                        data-year={item}
                        selected={item === year}
                        label={item}
                        onClick={() => onSelect(item)}
                    />
                ))}
            </div>
        </div>
    );
}

function MonthPane({ draft, max, lunar, onSelect }) {
    return (
        <div className="flex flex-col gap-2.5">
            <span className="text-[13px] font-semibold text-text-secondary">태어난 달을 선택하세요</span>
            <div role="group" aria-label="월" className="grid grid-cols-4 gap-1.5">
                {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                    <Cell
                        key={month}
                        selected={month === draft.month}
                        disabled={!lunar && compareDate({ year: draft.year, month, day: 1 }, max) > 0}
                        label={`${month}월`}
                        onClick={() => onSelect(month)}
                    />
                ))}
            </div>
        </div>
    );
}

function DayPane({ draft, max, lunar, onSelect }) {
    if (lunar) {
        return (
            <div className="flex flex-col gap-2.5">
                <span className="text-[13px] font-semibold text-text-secondary">태어난 날을 선택하세요(음력)</span>
                <div role="group" aria-label="일" className="grid grid-cols-7 gap-0.5">
                    {Array.from({ length: LUNAR_DAYS }, (_, index) => index + 1).map((day) => (
                        <Cell key={day} selected={day === draft.day} label={day} onClick={() => onSelect(day)} />
                    ))}
                </div>
            </div>
        );
    }
    const total = daysInMonth(draft.year, draft.month);
    const offset = new Date(draft.year, draft.month - 1, 1).getDay();
    return (
        <div className="flex flex-col gap-1">
            <div className="grid grid-cols-7 gap-0.5" aria-hidden="true">
                {WEEKDAYS.map((weekday) => (
                    <span key={weekday} className="py-1 text-center text-[12px] font-semibold text-text-secondary">
                        {weekday}
                    </span>
                ))}
            </div>
            <div role="group" aria-label="일" className="grid grid-cols-7 gap-0.5">
                {Array.from({ length: offset }, (_, index) => (
                    <span key={`pad-${index}`} />
                ))}
                {Array.from({ length: total }, (_, index) => index + 1).map((day) => (
                    <Cell
                        key={day}
                        selected={day === draft.day}
                        disabled={compareDate({ year: draft.year, month: draft.month, day }, max) > 0}
                        label={day}
                        onClick={() => onSelect(day)}
                    />
                ))}
            </div>
        </div>
    );
}
